/**
 * «منشی هوشمند» — a result opened (PM-0517). Opens only when the doctor has
 * something to read or to choose — a booking at ONE clinic goes straight to the
 * filled-in «ثبت نوبت» form and «پرونده» straight to the record (model.routeOf):
 *
 *   PROPOSAL at several clinics   the times, each with its clinic; one press opens the form
 *   CHOOSE_PATIENT                the candidates; a press runs the same words with the choice
 *   NOT_FOUND                     the closest names, the same way
 *   NO_CLINIC                     why there is nowhere to book, and the way to پذیرش
 *   ANSWER (the day)              the counts and who is next
 *   the rest                      what the secretary does and does not do
 *
 * Nothing here writes. The foot says whether the command cost پارسه.
 */
import React from 'react';
import { Alert, Button, Spinner, TaskOverlay } from '@parsina/framework';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

import { OUT_OF_SCOPE_TOPICS, UPCOMING_STATUSES, bookingHandoff, resultPatient } from './model';
import { useSecretaryStrings } from './secretaryStrings';

function PatientRow({ p, onPick, s, tr, testId }) {
  const facts = [
    p.fileNumber ? s('secretary.window.file', { file: tr.formatDigits(p.fileNumber) }) : null,
    Number.isFinite(p.age) ? s('secretary.window.age', { age: tr.formatNumber(p.age) }) : null,
    p.phoneTail ? s('secretary.window.phoneTail', { tail: tr.formatDigits(p.phoneTail) }) : null,
  ].filter(Boolean);
  return (
    <button
      type="button"
      onClick={onPick}
      data-testid={testId}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', borderRadius: 10,
        border: `1px solid ${C.border}`, background: C.card, cursor: 'pointer', textAlign: 'start', fontFamily: 'inherit',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary }}><bdi>{p.name}</bdi></span>
        {facts.length ? <span style={{ display: 'block', fontSize: '0.75rem', color: C.textMuted }}>{facts.join(' · ')}</span> : null}
      </span>
    </button>
  );
}

function SlotButton({ slot, onPick, s, tr, index }) {
  return (
    <button
      type="button"
      onClick={onPick}
      data-testid={`secretary-slot-${index}`}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', padding: '10px 12px',
        borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, cursor: 'pointer', fontFamily: 'inherit',
        color: C.textPrimary, fontSize: '0.875rem', fontWeight: 700,
      }}
    >
      <span>{s('secretary.window.slot', { day: tr.formatDate(slot.dateTime, 'weekdayDayMonth'), time: tr.formatTime(slot.dateTime) })}</span>
      {slot.clinicName ? <span style={{ fontSize: '0.78125rem', fontWeight: 600, color: C.textSecondary }}><bdi>{slot.clinicName}</bdi></span> : null}
    </button>
  );
}

function DaySummary({ d, s, tr }) {
  const n = tr.formatNumber;
  return (
    <div style={{ display: 'grid', gap: 10 }} data-testid="secretary-day">
      <div style={{ fontSize: '0.84375rem', color: C.textSecondary }}>
        {s('secretary.window.dayCounts', { completed: n(d.completed), arrived: n(d.arrived), scheduled: n(d.scheduled) })}
      </div>
      {d.requested > 0 ? <div style={{ fontSize: '0.8125rem', color: C.textSecondary }}>{s('secretary.window.dayRequested', { count: n(d.requested) })}</div> : null}
      {d.next?.length ? (
        <>
          <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>{s('secretary.window.dayNext')}</div>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {d.next.map((a) => (
              <li
                key={a.appointmentId}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: soft(C.navy, 0.04) }}
              >
                <span dir="ltr" style={{ minWidth: 48, fontWeight: 800, color: C.navy, fontVariantNumeric: 'tabular-nums' }}>
                  {a.dateTime ? tr.formatTime(a.dateTime) : s('secretary.window.noHour')}
                </span>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 700 }}><bdi>{a.patientName || s('secretary.window.unknownPatient')}</bdi></span>
                <span style={{ fontSize: '0.75rem', color: C.textMuted }}>
                  {UPCOMING_STATUSES.includes(a.status) ? s(`secretary.window.status.${a.status}`) : ''}
                  {a.clinicName ? ` · ${a.clinicName}` : ''}
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : <div style={{ fontSize: '0.84375rem', color: C.textSecondary }}>{s('secretary.window.dayNone')}</div>}
    </div>
  );
}

/** The window's title and body for one result. */
function content(item, { s, tr, onBook, onAnswer, onReception }) {
  const r = item.result;
  if (item.error === 'credit') return { title: s('secretary.notice.noCredit'), body: <p>{s('secretary.window.noCreditLine')}</p> };
  if (item.error === 'failed' || !r) return { title: s('secretary.notice.failed'), body: <p>{s('secretary.window.failedLine')}</p> };
  const name = resultPatient(r)?.name || r.heardName || r.detail || '';
  switch (r.outcome) {
    case 'PROPOSAL': {
      const b = r.booking;
      return {
        title: s(r.kind === 'follow_up' ? 'secretary.notice.followUpReady' : 'secretary.notice.bookReady', { name: b.patient.name }),
        body: (
          <div style={{ display: 'grid', gap: 8 }}>
            {b.requestedAt && b.requestedFree === false
              ? <p style={{ margin: 0 }}>{s('secretary.window.hourTaken', { time: tr.formatTime(b.requestedAt) })}</p>
              : <p style={{ margin: 0 }}>{s('secretary.window.pickSlot')}</p>}
            {b.slots.map((slot, i) => (
              <SlotButton key={`${slot.clinicId}-${slot.dateTime}`} slot={slot} index={i} s={s} tr={tr} onPick={() => onBook(bookingHandoff(b, i))} />
            ))}
          </div>
        ),
      };
    }
    case 'CHOOSE_PATIENT':
    case 'NOT_FOUND': {
      const choosing = r.outcome === 'CHOOSE_PATIENT';
      const offersNew = choosing && (r.kind === 'book_appointment' || r.kind === 'follow_up') && Boolean(r.heardName);
      return {
        title: choosing ? s('secretary.notice.choosePatient', { name: r.heardName || '' }) : s('secretary.notice.notFound', { name }),
        body: (
          <div style={{ display: 'grid', gap: 8 }}>
            <p style={{ margin: 0 }}>
              {choosing ? s('secretary.window.choosePatientLine', { name: r.heardName || '' }) : s('secretary.window.notFoundLine', { name })}
            </p>
            {!choosing && r.patients?.length ? <p style={{ margin: 0, fontWeight: 700 }}>{s('secretary.window.closest')}</p> : null}
            {(r.patients || []).filter((p) => p.id).map((p, i) => (
              <PatientRow key={p.id} p={p} s={s} tr={tr} testId={`secretary-patient-${i}`} onPick={() => onAnswer({ patientId: p.id })} />
            ))}
            {offersNew ? (
              <Button variant="outline" type="button" data-testid="secretary-new-patient" onClick={() => onAnswer({ newPatient: true })}>
                {s('secretary.window.newPatient')}
              </Button>
            ) : null}
          </div>
        ),
      };
    }
    case 'NO_SLOT':
      return {
        title: s('secretary.notice.noSlot'),
        body: (
          <div style={{ display: 'grid', gap: 10 }}>
            <p style={{ margin: 0 }}>{s('secretary.window.noSlotLine', { name })}</p>
            <Button type="button" onClick={() => onBook(bookingHandoff(r.booking, -1))}>{s('secretary.window.openForm')}</Button>
          </div>
        ),
      };
    case 'NO_CLINIC': {
      const lapsed = r.reason === 'read_only';
      return {
        title: s(lapsed ? 'secretary.window.readOnlyTitle' : 'secretary.window.noClinicTitle'),
        body: (
          <div style={{ display: 'grid', gap: 10 }} data-testid="secretary-no-clinic">
            <p style={{ margin: 0 }}>{s(lapsed ? 'secretary.window.readOnlyLine' : 'secretary.window.noClinicLine')}</p>
            {lapsed ? null : <Button variant="outline" type="button" onClick={onReception}>{s('secretary.window.goReception')}</Button>}
          </div>
        ),
      };
    }
    case 'ANSWER':
      if (r.daySummary) {
        return { title: s('secretary.window.dayTitle', { total: tr.formatNumber(r.daySummary.total) }), body: <DaySummary d={r.daySummary} s={s} tr={tr} /> };
      }
      break;
    case 'OUT_OF_SCOPE':
      return {
        title: s('secretary.window.outOfScopeTitle'),
        body: <p>{s(`secretary.window.topic.${OUT_OF_SCOPE_TOPICS.includes(r.topic) ? r.topic : 'other'}`)}</p>,
      };
    case 'UNAVAILABLE':
      return { title: s('secretary.notice.unavailable'), body: <p>{s('secretary.window.unavailableLine')}</p> };
    default:
      break;
  }
  return { title: s('secretary.notice.unknown'), body: <p>{s('secretary.window.unknownLine')}</p> };
}

export default function SecretaryWindow({ item, onClose, onBook, onAnswer, onRecordAgain, onReception, onTopUp }) {
  const s = useSecretaryStrings();
  const tr = useTranslation();
  const processing = item.status === 'processing';
  const { title, body } = processing
    ? { title: s('secretary.title'), body: <div style={{ display: 'grid', placeItems: 'center', padding: 24 }}><Spinner size={22} /></div> }
    : content(item, { s, tr, onBook, onAnswer, onReception });
  const r = item.result;
  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      {r && !processing ? (
        <span style={{ fontSize: '0.75rem', color: C.textMuted }} data-testid="secretary-charged" data-charged={r.charged ? 'true' : 'false'}>
          {s(r.charged ? 'secretary.window.charged' : 'secretary.window.notCharged')}
        </span>
      ) : null}
      <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 8 }}>
        {item.error === 'credit' && onTopUp ? <Button type="button" onClick={onTopUp}>{s('secretary.window.topUp')}</Button> : null}
        <Button variant="outline" type="button" onClick={onRecordAgain} disabled={processing} data-testid="secretary-record-again">
          {s('secretary.window.recordAgain')}
        </Button>
        <Button variant="ghost" type="button" onClick={onClose}>{s('secretary.window.close')}</Button>
      </span>
    </div>
  );
  return (
    <TaskOverlay open onClose={onClose} title={title} footer={footer} size="md" data-testid="secretary-window">
      <div style={{ display: 'grid', gap: 12, fontSize: '0.875rem', color: C.textPrimary, lineHeight: 1.8 }}>
        {item.text ? (
          <Alert variant="neutral" data-testid="secretary-heard">{s('secretary.notice.heard', { text: item.text })}</Alert>
        ) : null}
        {body}
      </div>
    </TaskOverlay>
  );
}
